import Vue from 'vue'
import { Module, ActionContext } from 'vuex'
import { RootState, CustomObjectsStore, CustomObjectsSchema } from './state_models'
import { awaitToJS } from '@/util/await-to-js'
import CustomObjectsServices from "../services/CustomObjectsServices";
import { StandardObjectKey, DefaultStandardObjectLabels } from '@/types/objects'
import { i18n } from '@/localization'

export const customObjectsStore: Module<CustomObjectsStore, RootState> = {
  namespaced: true,
  state: {
    schemas: [],
    schemaByKeys: {},
    currentSchema: null,
    betaLocations : [
    'PX8m5VwxEbcpFlzYEPVG',
    'eHy2cOSZxMQzQ6Yyvl8P',
    'sFjLQavq9wJ5DBV3MoTH',
    'iwf7ePmxExc2KYvIImnV',
    'tkHD7t0geSqyt3GJ43tK',
    'H2kaLV50kLd8QRJuAiDX',
    'Zfbrc1597T6JQyBUSLgY',
    'HAl6Ucp1pNHghMlNmOwT',
    '2Gr0A0p9xG6lBx85qUhd',
    'bG2xIA5je81fHvUzQJPW',
    'FHBIdSb3zuiUWjHzOaj9',
    'kmTvmv5WMC7HWkdOsvfy',
    'AEsTtNTP47n17HPpM64e',
    '3lFP4zBMsQ0PgWseUEEt',
    '3zZUrgmWC4QTDzNPClsi',
    'M9pL6GEQo7Q9eK6wTyyD',
    'dG3FsvCYnI8qISnp4jfv',
    'DiAO1ta8lTtZ6EnRO1WV',
    'LO9DmzE77UAs50t8Rax6',
    'PvZvpC22fVpwYtgeEKrj',
    'FkcHuxpCKD3EPVXdW4iB',
    'vU6RdLpapknkAv34TE1s',
    '8MYEW2l9khwJzEmFU6B2',
    'gqL7VIebNsVuZHMtqzbU',
    'B6eg7gFUNzWggRkwyglH',
    'omYInVDsux2hLiY8Nhew',
    '3lFP4zBMsQ0PgWseUEEt',
    'hhDkY22bnGQajourzeJl',
    'bvmcjwZFOGxlxb5S4oBD',
    'kAZKT6NkTqHcx3fQq0HD',
    'XQrphs7kOuPsob3xxfAg',
    'sfU0wI26hXqlsIRcDANA',
    'hr4tfDqNm4nHiJ5Aa4TG',
    'V90tL5TbCcpRC7kW0wEZ',
    '0kAct9zYeU27NOt3ovjx',
    'ytSxYH85ByNB2JICjn4F',
    'C6ZUt2xedwMGWosGUFaW',
    'du2eSfSzERECUeuBvXlo',
    'jlIMM7P8gmP85AhjbZHn',
    'OTzVLJMI3aHvXJhuMe5c',
    'gsgbkmONG8eAuFDIouaN',
    'wAcbT1ozUU5MKeOM1uT5',
    'qw3TVbqpskREZdoTHbsd',
    'JMNbtLe5diEGbmfg8YIk',
    'KQlfEly2p2GtzZCbkBJC',
    'qEzqL7oQf3n52MfSqZE8',
    'FQVRf3u21EbGGkVtXRUA',
    '7JbKhoN8gNMvmA09FelL',
    'dfg08aPdtlQ1RhIKkCnN',
    '62KWhnhVl69hgRVGt6gs',
    'OmLhg19rphwb5mSQbQlb',
    't4GsPWrodvBEEhu7YdSM',
    'EeGswtJ2E5Wk84F941dt',
    'EeGswtJ2E5Wk84F941dt',
    'isB08dnAtShsTKDsR9pY'
  ],
   betaAgencies : [
    'ktapXmiRvMM4pJHyN3b3',
    'YuTUZlUtrwBtvmgByZDW',
    'ALffYAeuPKDURxvyoafv',
    'QpYeHojXlwCz28caxi40',
    'AyZdVF1bdqzNLA3Xjuc9'
  ]},
  getters: {
    getAllCustomObjectSchemas: (state: CustomObjectsStore) => {
      return state?.schemas
    },
    getCustomObjectSchemas: (state: CustomObjectsStore) => {
      return state?.schemas.filter(o => o?.type === 'USER_DEFINED' || !o?.type)
    },
    getStandardObjectSchemas: (state: CustomObjectsStore) => {
      return state?.schemas.filter(o => o?.type === 'SYSTEM_DEFINED')
    },
    getByKey(state: CustomObjectsStore) {
      return (key: string) => {
        return state?.schemas.find(o => o.key === (o?.type === 'USER_DEFINED' ? `custom_objects.${key}` : key))
      }
    },
    getById(state: CustomObjectsStore) {
      return (id: string) => {
        return state?.schemas.find(o => o.id === id)
      }
    },
    getCurrentCustomObjectSchema(state: CustomObjectsStore) {
      return state.currentSchema
    },
    getObjects: (state: CustomObjectsStore) => {
      return state?.schemas
    },
    getObjectSingularLabelByKey: (state: CustomObjectsStore) => {
      return (key: string) => {
        const singularLabelInStore = state?.schemaByKeys[key]?.labels.singular || ""
        if (![StandardObjectKey.CONTACT, StandardObjectKey.BUSINESS, StandardObjectKey.OPPORTUNITY].includes(key as StandardObjectKey)) {
          return singularLabelInStore
        }
        if (singularLabelInStore !== DefaultStandardObjectLabels[key].singular) {
          return singularLabelInStore
        }
        return i18n.t(`common.standardObjectLabels.${key}.singular`)
      }
    },
    getObjectPluralLabelByKey: (state: CustomObjectsStore) => {
      return (key: string) => {
        const pluralLabelInStore = state?.schemaByKeys[key]?.labels.plural || ""
        if (![StandardObjectKey.CONTACT, StandardObjectKey.BUSINESS, StandardObjectKey.OPPORTUNITY].includes(key as StandardObjectKey)) {
          return pluralLabelInStore
        }
        if (pluralLabelInStore !== DefaultStandardObjectLabels[key].plural) {
          return pluralLabelInStore
        }
        return i18n.t(`common.standardObjectLabels.${key}.plural`)
      }
    },
    isCustomObjectEnabled (state, getters, rootState, rootGetters) {
      const company = rootGetters['company/get']
      const location = rootGetters["locations/getCurrentLocation"]
      const companyId = company?.id
      const locationId = location?.id
      const isLevelUpFeatureEnable = rootGetters['LevelUpDayFlag/isfeatursActive']
      const is497Plan = company && company.stripe_active_plan && company.stripe_active_plan.includes('497')
      const Beta_Locations = state.betaLocations;
      const Beta_Agencies = state.betaAgencies;

      if(Beta_Agencies.includes(companyId) || Beta_Locations.includes(locationId)) {
        return true
      }
      else return is497Plan && isLevelUpFeatureEnable

    }
  },
  mutations: {
    set: (state: CustomObjectsStore, payload: { schemas?: CustomObjectsSchema[] }) => {
      if (payload.schemas) {
        const schemasWithUniqueProps = payload.schemas.map(schema => ({
          ...schema,
          uniqueProperties: schema.uniqueProperties || []
        }))
        
        state.schemas = schemasWithUniqueProps
        state.schemaByKeys = schemasWithUniqueProps.reduce((acc, schema) => {
          acc[schema.key] = schema
          return acc
        }, {})
      }
    },
    setCurrentCustomObjectSchema: (
      state: CustomObjectsStore,
      payload: { schema?: CustomObjectsSchema, reset?: Boolean }
    ) => {
      if (payload.reset) {
        state.currentSchema = null
      } else if (payload.schema) {
        state.currentSchema = { fields: [...state.currentSchema?.fields || []], ...payload.schema }
      }
    },
    updateCustomObject: (
      state: CustomObjectsStore,
      payload: { key: string, updates: { [key: string]: any } }
    ) => {
      const customObject = state.schemaByKeys[payload.key];
      if (customObject) {
        Object.keys(payload.updates).forEach(field => {
          Vue.set(customObject, field, payload.updates[field]);
        });
      }
    }
  },
  actions: {
    async syncAll(context: ActionContext<CustomObjectsStore, RootState>, locationId: string) {
      if (!locationId) {
        return
      }

      const [err, response] = await awaitToJS(CustomObjectsServices.Schemas.getSchemasByLocationId(locationId))
      if (err) {
        return
      }

      context.commit('set', {
        schemas: response?.objects,
        schemaByKeys: response?.objects?.reduce((acc, schema) => {
          acc[schema.key] = schema
          return acc
        }, {})
      })
    },
  },
}

